import { Node, findParentNode, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { NodeRange } from '@tiptap/pm/model';
import { liftTarget } from '@tiptap/pm/transform';
import { Selection } from '@tiptap/pm/state';
import './grid.css';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      grid: {
         removeGridNode: () => ReturnType;
         toggleGrid: (columns: number | null) => ReturnType;
         setGridMode: (columns: number | null) => ReturnType;
         increaseColumns: () => ReturnType;
         decreaseColumns: () => ReturnType;
         nextGridCell: () => ReturnType;
      };
   }
}

export const Grid = Node.create<any, any>({
   name: 'grid',
   content: 'block*',
   group: 'block',
   selectable: true,
   renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes(HTMLAttributes, { class: 'wiki-grid' }), 0];
   },
   addNodeView() {
      return ReactNodeViewRenderer(Component);
   },
   addAttributes() {
      return {
         columns: {
            default: null,
            // Mirrors Text_Wiki_Render_Xhtml_Grid: a positive count drives the
            // custom property, anything else falls through to auto-fit.
            renderHTML: attributes => {
               const columns = Number.parseInt(attributes.columns, 10);
               return columns > 0
                  ? {
                       class: 'wiki-grid-columns',
                       style: `--wiki-grid-columns: ${columns}`,
                    }
                  : {};
            },
         },
      };
   },
   addKeyboardShortcuts() {
      return {
         Tab: ({ editor }) => editor.chain().focus().nextGridCell().run(),
      };
   },
   addCommands() {
      return {
         toggleGrid:
            (columns: number | null) =>
            ({ chain, editor }) => {
               if (editor.isActive(this.name)) {
                  return chain().focus().removeGridNode().run();
               }
               return chain()
                  .toggleWrap(this.name)
                  .updateAttributes(this.name, { columns: columns })
                  .focus()
                  .run();
            },
         setGridMode:
            (columns: number | null) =>
            ({ chain }) =>
               chain().focus().updateAttributes(this.name, { columns: columns }).run(),
         increaseColumns:
            () =>
            ({ chain, editor }) => {
               const current = editor.getAttributes(this.name).columns;
               return chain()
                  .updateAttributes(this.name, { columns: (current || 0) + 1 })
                  .focus()
                  .run();
            },
         decreaseColumns:
            () =>
            ({ chain, editor }) => {
               const current = editor.getAttributes(this.name).columns;
               if (!current || current <= 1) {
                  return false;
               }
               return chain()
                  .updateAttributes(this.name, { columns: current - 1 })
                  .focus()
                  .run();
            },
         // This is a raw command that is used by the editor commands above.
         removeGridNode:
            () =>
            ({ tr, state, dispatch }) => {
               const gridNodeInfo = findParentNode(node => node.type.name === this.name)(
                  state.selection
               );
               if (!gridNodeInfo) return false;
               const gridNode = gridNodeInfo.node;

               const start = tr.doc.resolve(gridNodeInfo.start);
               const end = tr.doc.resolve(gridNodeInfo.start + gridNode.content.size);
               const range = new NodeRange(start, end, gridNodeInfo.depth);
               const target = liftTarget(range);

               if (target === null) return false;
               if (dispatch) {
                  tr.lift(range, target);
               }
               return true;
            },
         nextGridCell:
            () =>
            ({ tr, state, dispatch }) => {
               const gridNodeInfo = findParentNode(node => node.type.name === this.name)(
                  state.selection
               );
               if (!gridNodeInfo) return false;
               const gridNode = gridNodeInfo.node;
               const currentPos = state.selection.from - gridNodeInfo.start;
               const currentChild = gridNode.childAfter(currentPos);
               const nextIndex = currentChild.index + 1;
               const nextChild = gridNode.maybeChild(nextIndex);
               if (nextChild) {
                  // try to move cursor into it
                  const start = tr.doc.resolve(gridNodeInfo.start);
                  const nextNodePos = start.posAtIndex(nextIndex);
                  const nextNodeResolvedPos = tr.doc.resolve(nextNodePos);
                  if (dispatch) {
                     const near = Selection.near(nextNodeResolvedPos);
                     tr.setSelection(near);
                  }
               } else {
                  // if current node is empty, refuse to add another.
                  const childNode = currentChild.node!;
                  const content = childNode.isText ? childNode.nodeSize : childNode.childCount;
                  if (content === 0) {
                     // delete the current (empty) node
                     const offset = currentChild.offset;
                     const deletePos = gridNodeInfo.start + offset;
                     if (dispatch) {
                        tr.delete(deletePos, deletePos + 2);
                     }
                     // move after the node
                     const nextPos = gridNodeInfo.start + gridNode.content.size;
                     const nextResolvedPos = tr.doc.resolve(nextPos);
                     if (dispatch) {
                        const near = Selection.near(nextResolvedPos);
                        tr.setSelection(near);
                     }
                     return true;
                  }
                  // insert new paragraph node
                  const newParagraph = state.schema.nodes.paragraph.create();
                  const insertPos = gridNodeInfo.start + gridNode.content.size;
                  if (dispatch) {
                     tr.insert(insertPos, newParagraph);
                     const start = tr.doc.resolve(insertPos);
                     const near = Selection.near(start);
                     tr.setSelection(near);
                  }
               }
               return true;
            },
      };
   },
});

const Component = (props: { node: { attrs: { columns?: number } } }) => (
   <NodeViewWrapper>
      <GridEditor columns={props.node.attrs.columns}>
         <NodeViewContent />
      </GridEditor>
   </NodeViewWrapper>
);

const GridEditor = ({ columns, children }: { columns?: number | null; children: any }) => {
   /*
    * NOTE: Keep this in step with packages/shared/ui/src/styles/wiki-grid.css.
    * The rendered page styles the cells directly; here each one is wrapped by
    * a node view, so the layout has to be restated around those wrappers.
    */
   if (columns === null) {
      return <div className="grid-node-view grid-node-view-auto">{children}</div>;
   }
   return (
      <div
         className="grid-node-view grid-node-view-columns"
         style={{ gridTemplateColumns: `repeat(${columns || 1}, 1fr)` }}
      >
         {children}
      </div>
   );
};
